import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import BuildingsGridToolbar from './buildings-grid-toolbar';
import DeleteBuildingDialog from './delete-buildings-dialog';
import { BuildingsModel } from '../../../../../types';
import AddBuildingDialog from './add-building-dialog';
import EditBuildingDialog from './edit-building-dialog';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { useQuery } from '@tanstack/react-query';
import { Messages } from '../../../../utils/message-constants';
import { getListOfBuildings, UserKeys } from '../../../../api/users-api';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../../shared/shared-data-grid-toolbar-helper';

import Tooltip from '@mui/material/Tooltip';
import Apartment from '@mui/icons-material/Apartment';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';

export default function BuildingsGrid() {
  const { calendarRoles, changeLogRoles, viewType } = useUserSettings();

  const [selectedBuilding, setSelectedBuilding] = useState<BuildingsModel>();
  const [openDeleteBuilding, setOpenDeleteBuilding] = useState(false);
  const [openCreateBuilding, setOpenCreateBuilding] = useState(false);
  const [openEditBuilding, setOpenEditBuilding] = useState(false);

  const {
    data: buildingsList,
    isLoading: buildingsLoading,
    isFetching: buildingsFetching,
  } = useQuery({
    queryKey: [UserKeys.BuildingsList],
    queryFn: () => getListOfBuildings(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('buildings'),
    },
  });

  const columns: GridColDef<BuildingsModel>[] = useMemo(
    () => [
      {
        field: 'buildingId',
        headerName: 'Id',
        flex: 1,
      },
      {
        field: 'buildingName',
        headerName: 'Name',
        flex: 1,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
      },
      {
        field: 'floorNames',
        headerName: 'Floor Names',
        disableColumnMenu: true,
        flex: 1,
        renderCell: (params) => {
          return params.row.floorNames.map((floor, idx) => {
            if (idx === params.row.floorNames.length - 1) {
              return floor;
            } else {
              return floor + ', ';
            }
          });
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params) => {
          const buttonArray = [];
          if (
            isAbleToSeeUIElement(
              calendarRoles || [],
              CalendarRoles.EditBuildings.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Edit"
                icon={
                  <Tooltip arrow title="Edit">
                    <Edit />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedBuilding(params.row);
                  setOpenEditBuilding(true);
                }}
                showInMenu
              />
            );
          }
          if (
            isAbleToSeeUIElement(
              calendarRoles || [],
              CalendarRoles.DeleteBuildings.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Delete"
                icon={
                  <Tooltip arrow title="Delete">
                    <Delete />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedBuilding(params.row);
                  setOpenDeleteBuilding(true);
                }}
                showInMenu
              />
            );
          }
          return buttonArray;
        },
      },
    ],
    [calendarRoles]
  );

  const toolbarProps = useMemo(
    () => ({
      calendarRoles: calendarRoles,
      setOpenCreateBuilding,
    }),
    [calendarRoles]
  );

  return (
    <>
      <SharedDataGridClient
        key="buildings-grid"
        gridKey={viewType.buildingsGridStates?.activeGridState}
        columns={columns}
        slots={{
          toolbar: useToolbar(BuildingsGridToolbar, toolbarProps),
          ...createGridOverlays('Buildings', <Apartment />),
        }}
        disableRowSelectionOnClick
        getRowId={(row) => row.buildingId}
        rows={buildingsList || []}
        loading={buildingsLoading || buildingsFetching}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
        }}
        gridStateKey="buildingsGridStates"
      />
      {openCreateBuilding && (
        <AddBuildingDialog
          open={openCreateBuilding}
          handleClose={() => setOpenCreateBuilding(false)}
          changeLogRoles={changeLogRoles}
        />
      )}
      {selectedBuilding && openEditBuilding ? (
        <EditBuildingDialog
          open={openEditBuilding}
          handleClose={() => setOpenEditBuilding(false)}
          selectedBuilding={selectedBuilding}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {selectedBuilding && openDeleteBuilding ? (
        <DeleteBuildingDialog
          open={openDeleteBuilding}
          handleClose={() => setOpenDeleteBuilding(false)}
          selectedBuilding={selectedBuilding}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
